import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card } from '../../components/ui/card';
import { Sparkles, Save, Plus, Trash2, Music, Users, Heart, Zap, Star, Image as ImageIcon } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminExperiencePage = () => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    experience_title_fr: '',
    experience_title_en: '',
    experience_title_nl: '',
    experience_title_es: '',
    experience_intro_fr: '',
    experience_intro_en: '',
    experience_intro_nl: '',
    experience_intro_es: '',
    hero_bg_url: '',
    features: [
      {
        id: 'feature-1',
        icon: 'music',
        title_fr: 'Musique',
        title_en: 'Music',
        title_nl: 'Muziek',
        title_es: 'Música',
        description_fr: 'Les meilleurs DJs de la scène reggaeton et latin trap',
        description_en: 'The best DJs from the reggaeton and latin trap scene',
        description_nl: 'De beste DJs van de reggaeton en latin trap scene',
        description_es: 'Los mejores DJs de la escena reggaeton y latin trap',
        order: 1
      },
      {
        id: 'feature-2',
        icon: 'sparkles',
        title_fr: 'Décor',
        title_en: 'Decor',
        title_nl: 'Decor',
        title_es: 'Decoración',
        description_fr: 'Une scénographie immersive et des effets visuels époustouflants',
        description_en: 'Immersive scenography and stunning visual effects',
        description_nl: 'Meeslepende scenografie en verbluffende visuele effecten',
        description_es: 'Escenografía inmersiva y efectos visuales impresionantes',
        order: 2
      },
      {
        id: 'feature-3',
        icon: 'users',
        title_fr: 'Communauté',
        title_en: 'Community',
        title_nl: 'Gemeenschap',
        title_es: 'Comunidad',
        description_fr: 'Une familia de 5000 personnes qui partagent la même passion',
        description_en: 'A familia of 5000 people sharing the same passion',
        description_nl: 'Een familia van 5000 mensen die dezelfde passie delen',
        description_es: 'Una familia de 5000 personas que comparten la misma pasión',
        order: 3
      }
    ]
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/experience`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching experience content:', error);
    }
  };

  const handleSave = async () => {
    const auth = localStorage.getItem('admin_auth');
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/experience`, content, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Contenu Experience sauvegardé !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateFeature = (index, field, value) => {
    const updatedFeatures = [...content.features];
    updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
    setContent({ ...content, features: updatedFeatures });
  };

  const addFeature = () => {
    const newFeature = {
      id: `feature-${Date.now()}`,
      icon: 'star',
      title_fr: '',
      title_en: '',
      title_nl: '',
      title_es: '',
      description_fr: '',
      description_en: '',
      description_nl: '',
      description_es: '',
      image_url: '',
      order: content.features.length + 1
    };
    setContent({ ...content, features: [...content.features, newFeature] });
  };

  const deleteFeature = (index) => {
    if (!window.confirm('Supprimer cette fonctionnalité ?')) return;
    const updatedFeatures = content.features.filter((_, i) => i !== index);
    setContent({ ...content, features: updatedFeatures });
  };

  const iconOptions = [
    { value: 'music', label: 'Music', icon: Music },
    { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
    { value: 'users', label: 'Users', icon: Users },
    { value: 'heart', label: 'Heart', icon: Heart },
    { value: 'zap', label: 'Zap', icon: Zap },
    { value: 'star', label: 'Star', icon: Star }
  ];

  return (
    <div data-testid="admin-experience-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Section Expérience</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez le contenu de la section Expérience (HomePage)</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Titre de la section */}
      <Card className="bg-white/5 border-white/10 p-6 mb-6">
        <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Titre de la section</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Français</label>
            <Input
              value={content.experience_title_fr}
              onChange={(e) => setContent({ ...content, experience_title_fr: e.target.value })}
              placeholder="L'Expérience Invasion Latina"
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">English</label>
            <Input
              value={content.experience_title_en}
              onChange={(e) => setContent({ ...content, experience_title_en: e.target.value })}
              placeholder="The Invasion Latina Experience"
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Nederlands</label>
            <Input
              value={content.experience_title_nl}
              onChange={(e) => setContent({ ...content, experience_title_nl: e.target.value })}
              placeholder="De Invasion Latina Ervaring"
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Español</label>
            <Input
              value={content.experience_title_es}
              onChange={(e) => setContent({ ...content, experience_title_es: e.target.value })}
              placeholder="La Experiencia Invasion Latina"
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
        </div>
      </Card>

      {/* Introduction */}
      <Card className="bg-white/5 border-white/10 p-6 mb-6">
        <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Introduction</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Français</label>
            <Textarea
              value={content.experience_intro_fr}
              onChange={(e) => setContent({ ...content, experience_intro_fr: e.target.value })}
              placeholder="Texte d'introduction..."
              rows={3}
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">English</label>
            <Textarea
              value={content.experience_intro_en}
              onChange={(e) => setContent({ ...content, experience_intro_en: e.target.value })}
              placeholder="Introduction text..."
              rows={3}
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Nederlands</label>
            <Textarea
              value={content.experience_intro_nl}
              onChange={(e) => setContent({ ...content, experience_intro_nl: e.target.value })}
              placeholder="Introductietekst..."
              rows={3}
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Español</label>
            <Textarea
              value={content.experience_intro_es}
              onChange={(e) => setContent({ ...content, experience_intro_es: e.target.value })}
              placeholder="Texto de introducción..."
              rows={3}
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
        </div>
      </Card>

      {/* Image de fond générale Expérience */}
      <Card className="bg-white/5 border-white/10 p-6 mb-6">
        <h2 className="font-display text-lg text-[#F5F0E8] mb-2 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-[#C9A84C]" />
          Image de fond — Section Expérience
        </h2>
        <p className="text-xs text-[#B9B2A8] mb-4">
          S'affiche à deux endroits&nbsp;: en grand en haut de la page <code className="text-[#C9A84C]">/experience</code>, et en fond flou derrière le titre «&nbsp;L'EXPÉRIENCE&nbsp;» sur la home. Recommandé&nbsp;: paysage 16:9, foule en soirée.
        </p>
        <ImageUploader
          label="Image de fond"
          value={content.hero_bg_url}
          onChange={(url) => setContent({ ...content, hero_bg_url: url })}
        />
      </Card>

      {/* Features */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg text-[#F5F0E8]">Fonctionnalités / Atouts</h2>
        <Button 
          onClick={addFeature}
          variant="outline"
          className="border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#060606]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      <div className="space-y-4">
        {content.features.map((feature, index) => {
          const IconComponent = iconOptions.find(opt => opt.value === feature.icon)?.icon || Star;
          
          return (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white/5 border-white/10 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#C9A84C] flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#060606]" />
                    </div>
                    <div>
                      <h3 className="font-display text-[#F5F0E8]">Fonctionnalité {index + 1}</h3>
                      <p className="text-xs text-[#B9B2A8]">Ordre: {feature.order}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteFeature(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Icône</label>
                    <Select 
                      value={feature.icon} 
                      onValueChange={(v) => updateFeature(index, 'icon', v)}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B0B0C] border-white/10">
                        {iconOptions.map(opt => {
                          const Icon = opt.icon;
                          return (
                            <SelectItem key={opt.value} value={opt.value} className="text-[#F5F0E8]">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {opt.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Ordre</label>
                    <Input
                      type="number"
                      value={feature.order}
                      onChange={(e) => updateFeature(index, 'order', parseInt(e.target.value) || 1)}
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                </div>

                {/* Titres dans les 4 langues */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre (FR)</label>
                    <Input
                      value={feature.title_fr}
                      onChange={(e) => updateFeature(index, 'title_fr', e.target.value)}
                      placeholder="Musique"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Title (EN)</label>
                    <Input
                      value={feature.title_en}
                      onChange={(e) => updateFeature(index, 'title_en', e.target.value)}
                      placeholder="Music"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titel (NL)</label>
                    <Input
                      value={feature.title_nl}
                      onChange={(e) => updateFeature(index, 'title_nl', e.target.value)}
                      placeholder="Muziek"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Título (ES)</label>
                    <Input
                      value={feature.title_es}
                      onChange={(e) => updateFeature(index, 'title_es', e.target.value)}
                      placeholder="Música"
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                </div>

                {/* Descriptions dans les 4 langues */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Description (FR)</label>
                    <Textarea
                      value={feature.description_fr}
                      onChange={(e) => updateFeature(index, 'description_fr', e.target.value)}
                      placeholder="Description en français..."
                      rows={2}
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Description (EN)</label>
                    <Textarea
                      value={feature.description_en}
                      onChange={(e) => updateFeature(index, 'description_en', e.target.value)}
                      placeholder="Description in English..."
                      rows={2}
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Beschrijving (NL)</label>
                    <Textarea
                      value={feature.description_nl}
                      onChange={(e) => updateFeature(index, 'description_nl', e.target.value)}
                      placeholder="Beschrijving in het Nederlands..."
                      rows={2}
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Descripción (ES)</label>
                    <Textarea
                      value={feature.description_es}
                      onChange={(e) => updateFeature(index, 'description_es', e.target.value)}
                      placeholder="Descripción en español..."
                      rows={2}
                      className="bg-white/5 border-white/10 text-[#F5F0E8]"
                    />
                  </div>
                </div>

                {/* Image de la fonctionnalité (affichée sur la page /experience) */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <ImageUploader
                    label="Image associée (page /experience)"
                    value={feature.image_url}
                    onChange={(url) => updateFeature(index, 'image_url', url)}
                  />
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminExperiencePage;
